import { CodeBook } from "@king-ict/eupisi-ui/models";
import { axiosBaseQuery } from "@king-ict/eupisi-ui/utils";
import { createApi } from "@reduxjs/toolkit/query/react";

import { CodeBookExtended, QualificationTypeCodeBook } from "../models/CodeBook";
import { ProgramCodeBook } from "../models/Program";

export const codeBookApi = createApi({
  reducerPath: "codeBook",
  baseQuery: axiosBaseQuery(),
  endpoints: (build) => ({
    getProgramTypes: build.query<CodeBook[], void>({
      query: () => "program-types",
    }),
    getProgramTypeDetails: build.query<CodeBookExtended[], void>({
      query: () => "program-types/detail"
    }),
    getProgramsNames: build.query<ProgramCodeBook[], void>({
      query: () => ({ url: "programs" }),
    }),
    getProgramsByType: build.query<ProgramCodeBook[], number>({
      query: (programTypeId) => ({
        url: "programs/by-type",
        data: { programTypeId: programTypeId, expires: "" }
      }),
    }),
    getProgramsByTypeAndDate: build.query<ProgramCodeBook[], { id: number, expires: string }>({
      query: (params) => ({
        url: "programs/by-type",
        data: { programTypeId: params.id, expires: params.expires }
      }),
    }),
    getFormsOfTeaching: build.query<CodeBook[], string | void>({
      query: (expires) => `forms-of-teaching/list/${expires ? expires : ""}`,
    }),
    getKnowledgeChecks: build.query<CodeBook[], string | void>({
      query: (expires) => `knowledge-checks/list/${expires ? expires : ""}`,
    }),
    getEnrollmentConditions: build.query<CodeBook[], string | void>({
      query: (expires) => `enrollment-conditions/list/${expires ? expires : ""}`,
    }),
    getLanguages: build.query<CodeBook[], string | void>({
      query: (expires) => `languages/${expires ? expires : ""}`,
    }),
    getLanguageLevels: build.query<CodeBook[], string | void>({
      query: (expires) => `language-levels/${expires ? expires : ""}`,
    }),
    getEducationSectors: build.query<CodeBook[], void>({
      query: () => "education-sectors",
    }),
    getEducationSubSectors: build.query<CodeBook[], number>({
      query: (id) => `education-sectors/${id}/education-sub-sectors`,
    }),
    getAllEducationSubSectors: build.query<CodeBook[], void>({
      query: () => `education-sectors/education-sub-sectors`,
    }),
    getCounties: build.query<CodeBook[], void>({
      query: () => `counties`,
    }),
    getProgramRequestStatuses: build.query<CodeBook[], void>({
      query: () => "program-request-statuses"
    }),

    getProgramRequestStatusesByUser: build.query<CodeBook[], number>({
      query: (role) => `program-request-statuses/${role}`,
    }),

    getProgramRequestStatusesForProcessingByUser: build.query<CodeBook[], number>({
      query: (role) => `program-request-statuses/for-processing/${role}`,
    }),

    getCommercialCourts: build.query<CodeBook[], void>({
      query: () => "commercial-courts"
    }),
    getHKOLevels: build.query<CodeBook[], void>({
      query: () => "hko-levels"
    }),
    getHKOLevelsByType: build.query<CodeBook[], number>({
      query: (programTypeId) => ({
        url: "hko-levels/by-type",
        data: { programTypeId: programTypeId }
      }),
    }),
    getQualifications: build.query<CodeBook[], void>({
      query: () => "qualifications",
    }),
    getQualificationTypes: build.query<CodeBook[], void>({
      query: () => "qualification-types",
    }),
    getQualificationTypesByProgram: build.query<QualificationTypeCodeBook[], number>({
      query: (id) => `qualification-types/${id}`,
    }),
    getProfessionFields: build.query<CodeBook[], void>({
      query: () => "profession-fields",
    }),
    getUserRoles: build.query<CodeBook[], void>({
      query: () => "roles"
    }),
    getNotificationRoles: build.query<CodeBook[], void>({
      query: () => "roles/get-notification-roles"      
    }),
    getInstitutionType: build.query<CodeBook[], void>({
      query: () => "institution-types"
    }),
    getUserInstitutions: build.query<CodeBook[], number>({
      query: (id) => `roles/user-instututions/${id}`,
    }),
    getGroupProgramRequestTypes: build.query<CodeBook[], void>({
      query: () => "group-program-requests/types"
    }),
    getProgramNamesCode: build.query<CodeBook[], void>({
      query: () => "programs/codebook"
    }),
    getProgramRequestTypes: build.query<CodeBook[], void>({
      query: () => "program-request-types"
    }),
    getProgramRequestTypesByUser: build.query<CodeBook[], number>({
      query: (role) => `program-request-types/${role}`
    })
  })
});

export const {
  useGetProgramTypesQuery,
  useGetProgramTypeDetailsQuery,
  useGetProgramsNamesQuery,
  useGetProgramsByTypeQuery,
  useGetProgramsByTypeAndDateQuery,
  useGetFormsOfTeachingQuery,
  useGetKnowledgeChecksQuery,
  useGetEnrollmentConditionsQuery,
  useGetLanguagesQuery,
  useGetLanguageLevelsQuery,
  useGetEducationSectorsQuery,
  useGetEducationSubSectorsQuery,
  useGetAllEducationSubSectorsQuery,
  useGetCountiesQuery,
  useGetProgramRequestStatusesQuery,
  useGetProgramRequestStatusesByUserQuery,
  useGetProgramRequestStatusesForProcessingByUserQuery,
  useGetCommercialCourtsQuery,
  useGetHKOLevelsQuery,
  useGetHKOLevelsByTypeQuery,
  useGetQualificationsQuery,
  useGetQualificationTypesQuery,
  useGetQualificationTypesByProgramQuery,
  useGetProfessionFieldsQuery,
  useGetUserRolesQuery,
  useGetNotificationRolesQuery,
  useGetInstitutionTypeQuery,
  useGetUserInstitutionsQuery,
  useGetGroupProgramRequestTypesQuery,
  useGetProgramNamesCodeQuery,
  useGetProgramRequestTypesQuery,
  useGetProgramRequestTypesByUserQuery
} = codeBookApi;
